import type {
  ResolveWidgetVehicleResponse,
  ServiceQuote,
  WidgetBootstrapResponse,
} from '../types/PublicBookingChannelTypes'
import { normalizePrice } from './format'
import type { BasketItem } from './services'

/**
 * Prices that depend on the car (ACA-96 price by vehicle, ACA-97 MOT class).
 *
 * Before the registration lookup a service the garage prices by vehicle has no
 * price to show, only "Price for your car after your reg". After it, the lookup's
 * quotes replace every service's price: an exact figure, or price on request when
 * the garage needs to confirm it. MOT services for a class the garage does not
 * test are left out altogether, so the customer never sees an MOT they cannot book.
 *
 * The server prices every booking again from the car, so nothing here decides
 * what is charged. It only keeps what the customer reads honest.
 */

/**
 * How a service's price reads: a figure, waiting for the reg, on request, or
 * waiting for the answer to "What is it?" (an MOT of unknown class, ACA-110).
 */
export type PriceState = 'fixed' | 'afterReg' | 'onRequest' | 'needsType'

export const PRICE_AFTER_REG = 'Price for your car after your reg'
export const PRICE_ON_REQUEST = 'Price on request'
export const PRICE_NEEDS_TYPE = 'Price once you say what it is'
/** A price HaynesPro worked out for this very car (ACA-60). */
export const PRICE_FOR_YOUR_CAR = 'Price for your car'
export const PRICE_CHANGED_MESSAGE = 'The price has changed. Please check it and book again.'

/** The words for a price with no figure yet. */
export function unconfirmedPriceLabel(state: PriceState | undefined): string {
  return state === 'afterReg' ? PRICE_AFTER_REG : state === 'needsType' ? PRICE_NEEDS_TYPE : PRICE_ON_REQUEST
}

/** A bootstrap service as the widget shows it for this car. */
export type VehiclePricedService = WidgetBootstrapResponse.ServiceItem & {
  priceState?: PriceState
  priceBasis?: ServiceQuote['priceBasis']
}

export function priceStateOf(
  service: WidgetBootstrapResponse.ServiceItem | null | undefined
): PriceState {
  return (service as VehiclePricedService | null | undefined)?.priceState ?? 'fixed'
}

/** True for a price the customer cannot pay online yet: any price with no figure. */
export function isUnconfirmedPrice(state: PriceState | undefined): boolean {
  return state != null && state !== 'fixed'
}

/** True when the lookup says the garage does not test this car's MOT class. */
export function motClassNotTested(vehicle: ResolveWidgetVehicleResponse | null): boolean {
  return vehicle?.motClassTested === false
}

/**
 * The polite line for a car whose MOT class the garage does not test, with the
 * garage's number when it has given one.
 */
export function motNotTestedMessage(contactPhone: string | null | undefined): string {
  const phone = contactPhone?.trim()
  return phone
    ? `Sorry, we don't test this type of vehicle. Please call us on ${phone}.`
    : "Sorry, we don't test this type of vehicle. Please call us."
}

/** A service the garage doesn't offer for this vehicle (ACA-109), refused at booking. */
export function serviceNotForVehicleMessage(contactPhone: string | null | undefined): string {
  const phone = contactPhone?.trim()
  return phone
    ? `Sorry, we can't book that for this vehicle. Please call us on ${phone}.`
    : "Sorry, we can't book that for this vehicle. Please call us."
}

/**
 * The channel's services as this car sees them.
 *
 * @param manual true once the customer has typed their vehicle's details
 *   because the lookup could not find it: the garage then confirms any price
 *   that depends on the car, as the server does with nothing known about it.
 */
export function servicesForVehicle(
  services: WidgetBootstrapResponse.ServiceItem[],
  vehicle: ResolveWidgetVehicleResponse | null,
  manual = false
): WidgetBootstrapResponse.ServiceItem[] {
  const quotes = vehicle?.quotes
  if (!quotes) {
    const waiting: PriceState = manual && !vehicle ? 'onRequest' : 'afterReg'
    return services.map((service): VehiclePricedService =>
      service.pricedByVehicle ? { ...service, price: null, priceState: waiting } : service
    )
  }

  // The server quotes every service it offers for this car and leaves out the rest (an MOT for a
  // class the garage doesn't test, whatever category it is filed under), so only a quoted service
  // is offered. Deciding it here instead, by category, let an MOT filed elsewhere through to a
  // booking the server then refused.
  const byId = new Map(quotes.map((quote) => [quote.serviceId, quote]))
  const result: WidgetBootstrapResponse.ServiceItem[] = []
  for (const service of services) {
    const quote = byId.get(service.id)
    if (!quote) continue
    const waiting: PriceState | null = quote.needsVehicleType
      ? 'needsType'
      : quote.priceOnRequest
        ? 'onRequest'
        : null
    const priced: VehiclePricedService = waiting
      ? { ...service, price: null, priceState: waiting }
      : { ...service, price: quote.price ?? null, priceState: 'fixed', priceBasis: quote.priceBasis }
    result.push(priced)
  }
  return result
}

/**
 * The basket with every line at its service's current price, and any line whose
 * service is no longer offered for this car taken out. The same array back when
 * nothing changed, so it can run in an effect without looping.
 */
export function repriceBasket(
  basket: BasketItem[],
  services: WidgetBootstrapResponse.ServiceItem[]
): BasketItem[] {
  const byId = new Map(services.map((service) => [service.id, service]))
  let changed = false
  const next: BasketItem[] = []
  for (const item of basket) {
    const service = byId.get(item.serviceId)
    if (!service) {
      changed = true
      continue
    }
    const price = normalizePrice(service.price)
    const priceState = priceStateOf(service)
    if (item.price !== price || (item.priceState ?? 'fixed') !== priceState) {
      changed = true
      next.push({ ...item, price, priceState })
    } else {
      next.push(item)
    }
  }
  return changed ? next : basket
}

/**
 * What the widget showed for each line of the basket, sent with the booking so
 * the server can refuse it when a price has changed (ACA-96). A line still to
 * be confirmed is sent as price on request; a tyre fitting shown as included
 * is sent at its own price, which is what the server prices it at.
 */
export function shownQuotes(basket: BasketItem[]): ServiceQuote[] {
  return basket.map((item) =>
    isUnconfirmedPrice(item.priceState)
      ? { serviceId: item.serviceId, price: null, priceOnRequest: true }
      : { serviceId: item.serviceId, price: item.price, priceOnRequest: false }
  )
}
